import type { Auth, AuthCredential, GoogleAuthProvider as GoogleAuthProviderType, User } from "firebase/auth";
import * as firebaseAuth from "firebase/auth";

type FirebaseAuthModule = typeof firebaseAuth;
let fb: FirebaseAuthModule | Partial<FirebaseAuthModule> = firebaseAuth;

// Test seam — do not call from product code.
export function __setFirebaseAuthForTests(stub: Partial<FirebaseAuthModule>): void {
    fb = { ...firebaseAuth, ...stub };
}

export type LinkResult =
    | { ok: true }
    | {
          ok: false;
          collision: {
              email: string | null;
              credential: AuthCredential | null;
              kind: "email" | "google";
          };
      };

const COLLISION_CODES = new Set(["auth/credential-already-in-use", "auth/email-already-in-use"]);

export async function linkWithEmail(user: User, email: string, password: string): Promise<LinkResult> {
    const credential = (fb.EmailAuthProvider as typeof firebaseAuth.EmailAuthProvider).credential(email, password);
    try {
        await (fb.linkWithCredential as typeof firebaseAuth.linkWithCredential)(user, credential);
        return { ok: true };
    } catch (error) {
        const err = error as { code?: string };
        if (err.code && COLLISION_CODES.has(err.code)) {
            return { ok: false, collision: { email, credential: null, kind: "email" } };
        }
        throw error;
    }
}

export async function linkWithGoogle(user: User, provider: GoogleAuthProviderType): Promise<LinkResult> {
    try {
        await (fb.linkWithPopup as typeof firebaseAuth.linkWithPopup)(user, provider);
        return { ok: true };
    } catch (error) {
        const err = error as {
            code?: string;
            credential?: AuthCredential;
            customData?: { email?: string };
        };
        if (err.code && COLLISION_CODES.has(err.code)) {
            const credential =
                err.credential ??
                (fb.GoogleAuthProvider as typeof firebaseAuth.GoogleAuthProvider).credentialFromError(error as never) ??
                null;
            return {
                ok: false,
                collision: {
                    email: err.customData?.email ?? null,
                    credential,
                    kind: "google",
                },
            };
        }
        throw error;
    }
}

export async function signInAfterCollision(
    auth: Auth,
    email: string | null,
    password: string | null,
    credential: AuthCredential | null
): Promise<void> {
    if (credential) {
        await (fb.signInWithCredential as typeof firebaseAuth.signInWithCredential)(auth, credential);
        return;
    }
    if (email && password) {
        await (fb.signInWithEmailAndPassword as typeof firebaseAuth.signInWithEmailAndPassword)(auth, email, password);
        return;
    }
    throw new Error("signInAfterCollision: no credential and no email/password supplied");
}
